/**
 * Módulo de Prospecção Ativa de Clientes
 * ======================================
 * Interface para gerenciar prospecção automatizada via WhatsApp
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Upload, Play, Pause, Trash2, Plus, Edit2, Save, X,
  Target, MessageSquare, Phone, Building2, MapPin, Calendar,
  TrendingUp, Bot, CheckCircle, XCircle, Clock, Filter,
  FileText, Send, Settings, BarChart3, RefreshCw, Search
} from 'lucide-react';
import { BACKEND_URL as API_URL } from './utils/apiConfig';

// Componente de Aba
const Tab = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 font-medium transition-all border-b-2 ${
      active 
        ? 'text-blue-400 border-blue-400 bg-blue-500/10' 
        : 'text-gray-400 border-transparent hover:text-gray-300 hover:bg-gray-800/50'
    }`}
  >
    {Icon && <Icon size={18} />}
    {children}
  </button>
);

// Card de Estatística
const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => (
  <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm">{title}</p>
        <p className={`text-2xl font-bold text-${color}-400`}>{value}</p>
        {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg bg-${color}-500/20`}>
        <Icon className={`text-${color}-400`} size={24} />
      </div>
    </div>
  </div>
);

// Badge de Status
const StatusBadge = ({ status }) => {
  const styles = {
    new: 'bg-gray-500/20 text-gray-300',
    contacted: 'bg-blue-500/20 text-blue-300',
    qualified: 'bg-yellow-500/20 text-yellow-300',
    interested: 'bg-purple-500/20 text-purple-300',
    converted: 'bg-green-500/20 text-green-300',
    rejected: 'bg-red-500/20 text-red-300',
    bot_detected: 'bg-orange-500/20 text-orange-300',
    draft: 'bg-gray-500/20 text-gray-300',
    active: 'bg-green-500/20 text-green-300',
    paused: 'bg-yellow-500/20 text-yellow-300',
    completed: 'bg-blue-500/20 text-blue-300'
  };

  const labels = {
    new: 'Novo',
    contacted: 'Contatado',
    qualified: 'Qualificado',
    interested: 'Interessado',
    converted: 'Convertido',
    rejected: 'Rejeitado',
    bot_detected: 'Bot Detectado',
    draft: 'Rascunho',
    active: 'Ativa',
    paused: 'Pausada',
    completed: 'Concluída'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
      {labels[status] || status}
    </span>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================

export default function ProspectionModule({ token }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados dos dados
  const [dashboard, setDashboard] = useState(null);
  const [segments, setSegments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [settings, setSettings] = useState(null);

  // Estados de UI
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [selectedSegmentFilter, setSelectedSegmentFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch helpers
  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
      throw new Error(error.detail || 'Erro na requisição');
    }
    return response.json();
  }, [token]);

  // Carregar dados
  const loadDashboard = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    }
  }, [fetchWithAuth]);

  const loadSegments = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/segments');
      setSegments(data.segments || []);
    } catch (err) {
      console.error('Erro ao carregar segmentos:', err);
    }
  }, [fetchWithAuth]);

  const loadLeads = useCallback(async () => {
    try {
      let endpoint = '/api/prospection/leads?limit=100';
      if (selectedSegmentFilter) endpoint += `&segment_id=${selectedSegmentFilter}`;
      if (selectedStatusFilter) endpoint += `&status=${selectedStatusFilter}`;
      
      const data = await fetchWithAuth(endpoint);
      setLeads(data.leads || []);
    } catch (err) {
      console.error('Erro ao carregar leads:', err);
    }
  }, [fetchWithAuth, selectedSegmentFilter, selectedStatusFilter]);

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/campaigns');
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Erro ao carregar campanhas:', err);
    }
  }, [fetchWithAuth]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/prospection/settings');
      setSettings(data);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    }
  }, [fetchWithAuth]);

  // Carregar dados iniciais
  useEffect(() => {
    loadDashboard();
    loadSegments();
    loadSettings();
  }, [loadDashboard, loadSegments, loadSettings]);

  // Recarregar leads quando filtros mudam
  useEffect(() => {
    if (activeTab === 'leads') {
      loadLeads();
    }
  }, [activeTab, loadLeads]);

  // Recarregar campanhas quando aba muda
  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadCampaigns();
    }
  }, [activeTab, loadCampaigns]);

  // Handlers
  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(null), 5000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleSaveSegment = async (segmentData) => {
    setLoading(true);
    try {
      if (editingSegment?.id) {
        await fetchWithAuth(`/api/prospection/segments/${editingSegment.id}`, {
          method: 'PUT',
          body: JSON.stringify(segmentData)
        });
        showMessage('Segmento atualizado com sucesso!');
      } else {
        await fetchWithAuth('/api/prospection/segments', {
          method: 'POST',
          body: JSON.stringify(segmentData)
        });
        showMessage('Segmento criado com sucesso!');
      }
      loadSegments();
      setShowSegmentModal(false);
      setEditingSegment(null);
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este segmento?')) return;
    
    setLoading(true);
    try {
      await fetchWithAuth(`/api/prospection/segments/${segmentId}`, { method: 'DELETE' });
      showMessage('Segmento excluído!');
      loadSegments();
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCSV = async (file, segmentId) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('segment_id', segmentId);

      const response = await fetch(`${API_URL}/api/prospection/leads/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);

      showMessage(`Importação concluída! ${data.imported} leads importados, ${data.duplicates} duplicados.`);
      loadLeads();
      loadDashboard();
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;
    
    try {
      await fetchWithAuth(`/api/prospection/leads/${leadId}`, { method: 'DELETE' });
      showMessage('Lead excluído!');
      loadLeads();
      loadDashboard();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleCampaignStatus = async (campaignId, status) => {
    try {
      await fetchWithAuth(`/api/prospection/campaigns/${campaignId}/status?status=${status}`, {
        method: 'PUT'
      });
      showMessage(`Campanha ${status === 'active' ? 'ativada' : status === 'paused' ? 'pausada' : status}!`);
      loadCampaigns();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleSaveSettings = async (newSettings) => {
    setLoading(true);
    try {
      await fetchWithAuth('/api/prospection/settings', {
        method: 'PUT',
        body: JSON.stringify(newSettings)
      });
      showMessage('Configurações salvas!');
      setSettings(newSettings);
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDERIZAÇÃO ====================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Target className="text-blue-400" />
            Prospecção Ativa
          </h2>
          <p className="text-gray-400 mt-1">Gerencie leads e campanhas de prospecção via WhatsApp</p>
        </div>
        <button
          onClick={() => { loadDashboard(); loadSegments(); loadLeads(); loadCampaigns(); }}
          className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300"
          title="Atualizar dados"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <XCircle size={20} />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-700 overflow-x-auto">
        <Tab active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} icon={BarChart3}>
          Dashboard
        </Tab>
        <Tab active={activeTab === 'segments'} onClick={() => setActiveTab('segments')} icon={Target}>
          Segmentos
        </Tab>
        <Tab active={activeTab === 'leads'} onClick={() => setActiveTab('leads')} icon={Users}>
          Leads
        </Tab>
        <Tab active={activeTab === 'campaigns'} onClick={() => setActiveTab('campaigns')} icon={Send}>
          Campanhas
        </Tab>
        <Tab active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings}>
          Configurações
        </Tab>
      </div>

      {/* Conteúdo das Tabs */}
      <div className="min-h-[500px]">
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <DashboardTab dashboard={dashboard} />
        )}

        {/* Segmentos */}
        {activeTab === 'segments' && (
          <SegmentsTab
            segments={segments}
            onAdd={() => { setEditingSegment(null); setShowSegmentModal(true); }}
            onEdit={(seg) => { setEditingSegment(seg); setShowSegmentModal(true); }}
            onDelete={handleDeleteSegment}
          />
        )}

        {/* Leads */}
        {activeTab === 'leads' && (
          <LeadsTab
            leads={leads}
            segments={segments}
            selectedSegment={selectedSegmentFilter}
            selectedStatus={selectedStatusFilter}
            searchTerm={searchTerm}
            onSegmentChange={setSelectedSegmentFilter}
            onStatusChange={setSelectedStatusFilter}
            onSearchChange={setSearchTerm}
            onImport={handleImportCSV}
            onDelete={handleDeleteLead}
            loading={loading}
          />
        )}

        {/* Campanhas */}
        {activeTab === 'campaigns' && (
          <CampaignsTab
            campaigns={campaigns}
            segments={segments}
            onAdd={() => setShowCampaignModal(true)}
            onStatusChange={handleCampaignStatus}
          />
        )}

        {/* Configurações */}
        {activeTab === 'settings' && (
          <SettingsTab
            settings={settings}
            onSave={handleSaveSettings}
            loading={loading}
          />
        )}
      </div>

      {/* Modal de Segmento */}
      {showSegmentModal && (
        <SegmentModal
          segment={editingSegment}
          onSave={handleSaveSegment}
          onClose={() => { setShowSegmentModal(false); setEditingSegment(null); }}
          loading={loading}
        />
      )}

      {/* Modal de Campanha */}
      {showCampaignModal && (
        <CampaignModal
          segments={segments}
          token={token}
          onClose={() => setShowCampaignModal(false)}
          onSuccess={() => { loadCampaigns(); setShowCampaignModal(false); showMessage('Campanha criada!'); }}
        />
      )}
    </div>
  );
}

// ==================== COMPONENTES DAS TABS ====================

function DashboardTab({ dashboard }) {
  if (!dashboard) {
    return <div className="text-center py-10 text-gray-400">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total de Leads"
          value={dashboard.total_leads}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Taxa de Conversão"
          value={`${dashboard.conversion_rate}%`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Bots Detectados"
          value={`${dashboard.bot_detection_rate}%`}
          icon={Bot}
          color="orange"
        />
        <StatCard
          title="Campanhas Ativas"
          value={dashboard.active_campaigns}
          icon={Send}
          color="purple"
        />
      </div>

      {/* Leads por Status */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h3 className="text-lg font-semibold text-white mb-4">Leads por Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {[
            { key: 'new', label: 'Novos', color: 'gray' },
            { key: 'contacted', label: 'Contatados', color: 'blue' },
            { key: 'qualified', label: 'Qualificados', color: 'yellow' },
            { key: 'interested', label: 'Interessados', color: 'purple' },
            { key: 'converted', label: 'Convertidos', color: 'green' },
            { key: 'rejected', label: 'Rejeitados', color: 'red' },
            { key: 'bot_detected', label: 'Bots', color: 'orange' }
          ].map(({ key, label, color }) => (
            <div key={key} className="text-center">
              <p className={`text-2xl font-bold text-${color}-400`}>
                {dashboard.leads_by_status[key] || 0}
              </p>
              <p className="text-gray-400 text-sm">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Leads por Segmento */}
      {dashboard.leads_by_segment?.length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4">Leads por Segmento</h3>
          <div className="space-y-3">
            {dashboard.leads_by_segment.map((seg, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-gray-300">{seg._id || 'Sem segmento'}</span>
                <span className="text-blue-400 font-semibold">{seg.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentsTab({ segments, onAdd, onEdit, onDelete }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-gray-400">{segments.length} segmento(s) cadastrado(s)</p>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus size={18} />
          Novo Segmento
        </button>
      </div>

      <div className="grid gap-4">
        {segments.map(segment => (
          <div
            key={segment.id}
            className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 hover:border-gray-600 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Target size={18} className="text-blue-400" />
                  {segment.name}
                </h3>
                {segment.description && (
                  <p className="text-gray-400 text-sm mt-1">{segment.description}</p>
                )}
                
                <div className="mt-4 space-y-2">
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Personalidade da IA:</span>
                    <p className="text-gray-300 text-sm">{segment.ai_personality}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Mensagens de Abertura:</span>
                    <ul className="text-gray-300 text-sm list-disc list-inside">
                      {segment.ai_opening_messages?.slice(0, 2).map((msg, idx) => (
                        <li key={idx}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => onEdit(segment)}
                  className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300"
                  title="Editar"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => onDelete(segment.id)}
                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400"
                  title="Excluir"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {segments.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            <Target size={48} className="mx-auto mb-3 opacity-50" />
            <p>Nenhum segmento cadastrado</p>
            <p className="text-sm">Crie segmentos para organizar seus leads por tipo de negócio</p>
          </div>
        )}
      </div>
    </div>
  );
}

function LeadsTab({ leads, segments, selectedSegment, selectedStatus, searchTerm, 
                    onSegmentChange, onStatusChange, onSearchChange, onImport, onDelete, loading }) {
  const [importSegment, setImportSegment] = useState('');
  const fileInputRef = React.useRef(null);

  const filteredLeads = leads.filter(lead => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchPhone = lead.phone?.toLowerCase().includes(search);
      const matchName = lead.business_name?.toLowerCase().includes(search);
      const matchCity = lead.city?.toLowerCase().includes(search);
      if (!matchPhone && !matchName && !matchCity) return false;
    }
    return true;
  });

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && importSegment) {
      onImport(file, importSegment);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtros e Ações */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm w-48"
            />
          </div>
          
          <select
            value={selectedSegment}
            onChange={(e) => onSegmentChange(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">Todos os segmentos</option>
            {segments.map(seg => (
              <option key={seg.id} value={seg.id}>{seg.name}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => onStatusChange(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">Todos os status</option>
            <option value="new">Novos</option>
            <option value="contacted">Contatados</option>
            <option value="qualified">Qualificados</option>
            <option value="interested">Interessados</option>
            <option value="converted">Convertidos</option>
            <option value="rejected">Rejeitados</option>
            <option value="bot_detected">Bots</option>
          </select>
        </div>

        {/* Importar CSV */}
        <div className="flex items-center gap-2">
          <select
            value={importSegment}
            onChange={(e) => setImportSegment(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">Selecione o segmento...</option>
            {segments.map(seg => (
              <option key={seg.id} value={seg.id}>{seg.name}</option>
            ))}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={!importSegment || loading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Upload size={18} />
            Importar CSV
          </button>
        </div>
      </div>

      {/* Info do formato CSV */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-300">
        <strong>Formato do CSV:</strong> phone,business_name,city,state,source,notes
      </div>

      {/* Lista de Leads */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
              <th className="pb-3 pr-4">Telefone</th>
              <th className="pb-3 pr-4">Estabelecimento</th>
              <th className="pb-3 pr-4">Segmento</th>
              <th className="pb-3 pr-4">Cidade</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3 pr-4">Criado em</th>
              <th className="pb-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map(lead => (
              <tr key={lead.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                <td className="py-3 pr-4">
                  <span className="flex items-center gap-2 text-white">
                    <Phone size={14} className="text-gray-500" />
                    {lead.phone}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className="flex items-center gap-2 text-gray-300">
                    <Building2 size={14} className="text-gray-500" />
                    {lead.business_name || '-'}
                  </span>
                </td>
                <td className="py-3 pr-4 text-gray-400">{lead.segment_name}</td>
                <td className="py-3 pr-4">
                  <span className="flex items-center gap-1 text-gray-400">
                    <MapPin size={14} />
                    {lead.city || '-'}{lead.state ? `, ${lead.state}` : ''}
                  </span>
                </td>
                <td className="py-3 pr-4"><StatusBadge status={lead.status} /></td>
                <td className="py-3 pr-4 text-gray-500 text-sm">
                  {lead.created_at ? new Date(lead.created_at).toLocaleDateString('pt-BR') : '-'}
                </td>
                <td className="py-3">
                  <button
                    onClick={() => onDelete(lead.id)}
                    className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400"
                    title="Excluir"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredLeads.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            <Users size={48} className="mx-auto mb-3 opacity-50" />
            <p>Nenhum lead encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignsTab({ campaigns, segments, onAdd, onStatusChange }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-gray-400">{campaigns.length} campanha(s)</p>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus size={18} />
          Nova Campanha
        </button>
      </div>

      <div className="grid gap-4">
        {campaigns.map(campaign => (
          <div
            key={campaign.id}
            className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Send size={18} className="text-purple-400" />
                  {campaign.name}
                  <StatusBadge status={campaign.status} />
                </h3>
                {campaign.description && (
                  <p className="text-gray-400 text-sm mt-1">{campaign.description}</p>
                )}
                
                <div className="flex gap-6 mt-4 text-sm">
                  <div>
                    <span className="text-gray-500">Total de Leads:</span>
                    <span className="text-white ml-2">{campaign.total_leads}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Contatados:</span>
                    <span className="text-blue-400 ml-2">{campaign.contacted}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Convertidos:</span>
                    <span className="text-green-400 ml-2">{campaign.converted}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Limite Diário:</span>
                    <span className="text-white ml-2">{campaign.daily_limit}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                {campaign.status === 'active' ? (
                  <button
                    onClick={() => onStatusChange(campaign.id, 'paused')}
                    className="flex items-center gap-1 px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg"
                  >
                    <Pause size={16} />
                    Pausar
                  </button>
                ) : campaign.status !== 'completed' && (
                  <button
                    onClick={() => onStatusChange(campaign.id, 'active')}
                    className="flex items-center gap-1 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg"
                  >
                    <Play size={16} />
                    Ativar
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {campaigns.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            <Send size={48} className="mx-auto mb-3 opacity-50" />
            <p>Nenhuma campanha criada</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsTab({ settings, onSave, loading }) {
  const [formData, setFormData] = useState(settings || {});

  useEffect(() => {
    if (settings) setFormData(settings);
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!settings) {
    return <div className="text-center py-10 text-gray-400">Carregando...</div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Personalização do Vendedor */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h3 className="text-lg font-semibold text-white mb-4">Personalização do Vendedor</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome do Vendedor</label>
            <input
              type="text"
              value={formData.seller_name || ''}
              onChange={(e) => handleChange('seller_name', e.target.value)}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              placeholder="Ex: João"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome da Empresa</label>
            <input
              type="text"
              value={formData.company_name || ''}
              onChange={(e) => handleChange('company_name', e.target.value)}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              placeholder="Ex: Meucardapiozap"
            />
          </div>
        </div>
      </div>

      {/* Detecção de Bot */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h3 className="text-lg font-semibold text-white mb-4">Detecção de Bot</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Tempo limite para detectar bot (ms)
            </label>
            <input
              type="number"
              value={formData.bot_response_threshold_ms || 3000}
              onChange={(e) => handleChange('bot_response_threshold_ms', parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            />
            <p className="text-gray-500 text-xs mt-1">Respostas mais rápidas que isso serão consideradas de bot</p>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Palavras-chave de Bot</label>
            <textarea
              value={formData.bot_keywords?.join('\n') || ''}
              onChange={(e) => handleChange('bot_keywords', e.target.value.split('\n').filter(k => k.trim()))}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white h-32"
              placeholder="Uma palavra por linha..."
            />
          </div>
        </div>
      </div>

      {/* Limites */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h3 className="text-lg font-semibold text-white mb-4">Limites</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Máximo de envios/dia</label>
            <input
              type="number"
              value={formData.max_daily_sends || 100}
              onChange={(e) => handleChange('max_daily_sends', parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Tentativas por lead</label>
            <input
              type="number"
              value={formData.max_retries_per_lead || 3}
              onChange={(e) => handleChange('max_retries_per_lead', parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            />
          </div>
        </div>
      </div>

      <button
        onClick={() => onSave(formData)}
        disabled={loading}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Save size={18} />
        {loading ? 'Salvando...' : 'Salvar Configurações'}
      </button>
    </div>
  );
}

// ==================== MODAIS ====================

function SegmentModal({ segment, onSave, onClose, loading }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ai_personality: 'Vendedor amigável e consultivo',
    ai_opening_messages: ['Boa noite! Vocês trabalham com delivery?'],
    ai_pitch_message: 'Que legal! Deixa eu me apresentar...',
    audio_url: '',
    video_url: '',
    ...segment
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) return;
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white">
            {segment ? 'Editar Segmento' : 'Novo Segmento'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome do Segmento *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="Ex: Hamburgueria"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrição</label>
            <input
              type="text"
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="Ex: Hamburguerias e lanchonetes"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Personalidade da IA</label>
            <input
              type="text"
              value={formData.ai_personality}
              onChange={(e) => handleChange('ai_personality', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Mensagens de Abertura (filtro)</label>
            <textarea
              value={formData.ai_opening_messages?.join('\n') || ''}
              onChange={(e) => handleChange('ai_opening_messages', e.target.value.split('\n').filter(m => m.trim()))}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white h-24"
              placeholder="Uma mensagem por linha..."
            />
            <p className="text-gray-500 text-xs mt-1">Mensagens usadas para filtrar se o lead já tem automação</p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Mensagem de Pitch</label>
            <textarea
              value={formData.ai_pitch_message}
              onChange={(e) => handleChange('ai_pitch_message', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white h-24"
              placeholder="Mensagem enviada após qualificar o lead..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">URL do Áudio</label>
              <input
                type="url"
                value={formData.audio_url || ''}
                onChange={(e) => handleChange('audio_url', e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">URL do Vídeo</label>
              <input
                type="url"
                value={formData.video_url || ''}
                onChange={(e) => handleChange('video_url', e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg flex items-center justify-center gap-2"
            >
              <Save size={18} />
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CampaignModal({ segments, token, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    segment_ids: [],
    daily_limit: 50,
    start_hour: 9,
    end_hour: 21,
    min_interval: 5,
    max_interval: 30,
    delay_before_pitch_hours: 2
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSegment = (segmentId) => {
    setFormData(prev => ({
      ...prev,
      segment_ids: prev.segment_ids.includes(segmentId)
        ? prev.segment_ids.filter(id => id !== segmentId)
        : [...prev.segment_ids, segmentId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || formData.segment_ids.length === 0) {
      setError('Preencha o nome e selecione pelo menos um segmento');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/prospection/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white">Nova Campanha</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-500/20 text-red-300 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome da Campanha *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="Ex: Prospecção Hamburguerias Janeiro"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrição</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Segmentos *</label>
            <div className="space-y-2">
              {segments.map(seg => (
                <label key={seg.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.segment_ids.includes(seg.id)}
                    onChange={() => toggleSegment(seg.id)}
                    className="rounded border-gray-600 bg-gray-800 text-blue-500"
                  />
                  <span className="text-white">{seg.name}</span>
                </label>
              ))}
              {segments.length === 0 && (
                <p className="text-gray-500 text-sm">Crie segmentos primeiro</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Limite/dia</label>
              <input
                type="number"
                value={formData.daily_limit}
                onChange={(e) => handleChange('daily_limit', parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                min={1}
                max={200}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Início (hora)</label>
              <input
                type="number"
                value={formData.start_hour}
                onChange={(e) => handleChange('start_hour', parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                min={0}
                max={23}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Fim (hora)</label>
              <input
                type="number"
                value={formData.end_hour}
                onChange={(e) => handleChange('end_hour', parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                min={0}
                max={23}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg"
            >
              {loading ? 'Criando...' : 'Criar Campanha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
